import {
  CalendarBlankIcon,
  GasPumpIcon,
  GearSixIcon,
  LockSimpleIcon,
  MapPinIcon,
  PathIcon,
  SealCheckIcon,
} from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Badge, Media, Tooltip } from '@shared/ui';

import {
  formatMileage,
  formatObservedDate,
  formatPrice,
  fuelLabelKey,
  registrationYear,
  transmissionLabelKey,
} from './lib';
import { isTeaser } from './types';
import type { VehicleCardView } from './types';
import styles from './VehicleCard.module.css';

interface VehicleCardProps {
  /**
   * Автомобиль в одном из трёх уровней доступа (BE-580). Карточка рисует
   * только данные объявления: аналитику она не принимает вовсе, поэтому
   * открытая карточка витрины не может её показать даже по ошибке.
   */
  vehicle: VehicleCardView;
  /** Позиция в сетке: первые карточки грузят фотографию немедленно. */
  index?: number | undefined;
  /** Куда ведёт карточка. Без него карточка не ссылка: адреса по умолчанию нет. */
  to?: string | undefined;
  /**
   * Плашка «Geprüft»: CarViQ.DE **задал продавцу вопросы и получил ответы**.
   * Не «CarViQ.DE проверил автомобиль» — такого вывода система делать не вправе
   * (MVP_PRD §2). В закрытом варианте плашки нет: работа ещё не оплачена.
   */
  interviewed?: boolean | undefined;
  /** Слот оценки: Match Score, рейтинг, отклонение от медианы. */
  score?: ReactNode | undefined;
  /** Слот действия во всю ширину («Details ansehen», «Auto auswählen»). */
  action?: ReactNode | undefined;
  /** Слот под ценой: причины соответствия, признаки внимания. */
  footnote?: ReactNode | undefined;
  /**
   * Действие поверх фотографии справа — сердечко избранного (Epic 58, `FE-586`).
   *
   * Стоит **вне** ссылки карточки: кнопка внутри `<a>` — вложенный
   * интерактивный элемент, который клавиатура и скринридер не различают.
   * Поверх снимка разрешено только действие, относящееся к самому объявлению
   * (DESIGN_SYSTEM §5.5).
   */
  favorite?: ReactNode | undefined;
  /** Сообщение о сбое действия на этой карточке (`role="alert"` задаёт вызывающий). */
  notice?: ReactNode | undefined;
}

const NO_DATA_KEY = 'catalog.noData';

/**
 * Карточка автомобиля (FE-194, DESIGN_SYSTEM §5.5).
 *
 * Единый компонент для каталога, результатов, четвёрки и заключений:
 * различается набором слотов, а не разметкой. Копии карточки неизбежно
 * разошлись бы по цвету цены, высоте и составу полей.
 *
 * Правила закрытого варианта — вопрос безопасности, а не вёрстки:
 *
 * * **закрытых значений нет в разметке.** `blur`, `opacity`, `color:
 *   transparent`, «XX XXX €» и любое другое замазывание запрещены: значение
 *   видно в DOM. Закрытое поле не приходит с сервера, и рисовать его нечем;
 * * **плейсхолдер не имитирует данные.** «≈ 25 000 €» и «от 20 000» — обещание
 *   конкретного числа, которого пользователь не получал;
 * * **карточка не выглядит сломанной**: высота закрытого и полного вида
 *   совпадает, сетка не прыгает после оплаты.
 */
export function VehicleCard({
  vehicle,
  index = 0,
  to,
  interviewed = false,
  score,
  action,
  footnote,
  favorite,
  notice,
}: VehicleCardProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'de';

  const teaser = isTeaser(vehicle);
  const year = teaser
    ? vehicle.first_registration_year
    : registrationYear(vehicle.first_registration_date);
  // Дата актуальности — часть объявления и в закрытом виде не приходит.
  const observedAt = teaser ? null : formatObservedDate(vehicle.data_observed_at, locale);
  const noData = t(NO_DATA_KEY);

  // Alt приходит от импорта каталога; если его нет — описываем то, что точно
  // известно. Пустой alt для содержательной фотографии недопустим.
  const imageAlt = vehicle.cover_image?.alt ?? `${vehicle.make} ${vehicle.model}`;
  /**
   * Куда ведёт карточка — **только по явному указанию** через `to`.
   *
   * Раньше открытая карточка по умолчанию вела на публичную страницу автомобиля
   * `/cars/:slug`. Этот маршрут удалён вместе с каталогом (решение заказчика
   * 2026-08-27), и адреса по умолчанию у карточки больше нет: внутри заказа его
   * задаёт `SelectionCard` (персональная страница автомобиля), а в закрытой
   * витрине ссылки нет вовсе — притворяться ссылкой карточка не должна.
   */
  const href = to ?? null;

  const body = (
    <>
      <div className={styles.mediaWrap}>
        <Media
          src={vehicle.cover_image?.card_url ?? undefined}
          alt={imageAlt}
          ratio="16 / 10"
          priority={index < 2}
          sizes="(max-width: 767px) 100vw, (max-width: 1023px) 50vw, 25vw"
        />
        <span className={styles.badgeSlot}>
          {/* Бейджа «Geprüft» в закрытом варианте нет: он относится к работе,
              которая ещё не оплачена и не выполнена.

              Отметки «лучший выбор» здесь тоже нет — она живёт над карточкой,
              в разметке экрана четвёрки: бейдж поверх снимка закрывает часть
              автомобиля, то есть данные, ради которых снимок и показан. */}
          {!teaser && interviewed ? (
            <Tooltip content={t('catalog.interviewedHint')}>
              <Badge tone="action" icon={<SealCheckIcon weight="regular" />}>
                {t('catalog.interviewed')}
              </Badge>
            </Tooltip>
          ) : null}
          {!teaser && vehicle.is_new ? (
            <Badge tone="neutral">
              {t('catalog.new')}
              {/* Уточнение термина: «Новое» — про публикацию на CarViQ.DE, а не про
                  новый автомобиль (MVP_PRD §2). */}
              <span className="visually-hidden"> — {t('catalog.newHint')}</span>
            </Badge>
          ) : null}
        </span>
      </div>

      <div className={styles.body}>
        <h3 className={styles.name}>
          {vehicle.make} {vehicle.model}
        </h3>
        {/* Модификация — часть объявления и в закрытом виде не показывается. */}
        <p className={styles.variant}>{teaser ? ' ' : (vehicle.variant ?? noData)}</p>

        <dl className={styles.specs}>
          <div>
            <dt>
              <CalendarBlankIcon size={16} aria-hidden="true" />
              <span className="visually-hidden">{t('catalog.spec.year')}</span>
            </dt>
            <dd className="tabular">{year ?? noData}</dd>
          </div>
          <div>
            <dt>
              <PathIcon size={16} aria-hidden="true" />
              <span className="visually-hidden">{t('catalog.spec.mileage')}</span>
            </dt>
            <dd className="tabular">
              {t('catalog.spec.mileageValue', {
                value: formatMileage(vehicle.mileage_km, locale),
              })}
            </dd>
          </div>
          {teaser ? null : (
            <>
              <div>
                <dt>
                  <GasPumpIcon size={16} aria-hidden="true" />
                  <span className="visually-hidden">{t('catalog.spec.fuel')}</span>
                </dt>
                <dd>{t(fuelLabelKey(vehicle.fuel_type), { defaultValue: vehicle.fuel_type })}</dd>
              </div>
              <div>
                <dt>
                  <GearSixIcon size={16} aria-hidden="true" />
                  <span className="visually-hidden">{t('catalog.spec.transmission')}</span>
                </dt>
                <dd>
                  {t(transmissionLabelKey(vehicle.transmission), {
                    defaultValue: vehicle.transmission,
                  })}
                </dd>
              </div>
            </>
          )}
        </dl>

        {score ? <div className={styles.score}>{score}</div> : null}

        <div className={styles.footer}>
          {teaser ? (
            /* Вместо цены — нейтральная плашка с замком. Правдоподобного
               плейсхолдера здесь быть не может: цена не приходит с сервера. */
            <p className={styles.locked}>
              <LockSimpleIcon size={16} weight="fill" aria-hidden="true" />
              {t('catalog.lockedPrice')}
            </p>
          ) : (
            <>
              <p className={`${styles.price} tabular`}>{formatPrice(vehicle.price, locale)}</p>
              <p className={styles.city}>
                <MapPinIcon size={15} aria-hidden="true" />
                <span className="visually-hidden">{t('catalog.spec.location')}</span>
                {vehicle.location.city ?? t('catalog.noData')}
              </p>
            </>
          )}
        </div>

        {footnote ? <div className={styles.footnote}>{footnote}</div> : null}

        {/* Дата актуальности обязательна там, где показаны данные объявления:
            в момент просмотра ничего не перезапрашивается, и пользователь
            должен видеть их возраст. В закрытом виде показывать нечего, но
            строка остаётся: высота карточки до и после оплаты совпадает, и
            сетка не прыгает (DESIGN_SYSTEM §5.5). */}
        <p className={styles.observed}>
          {teaser
            ? '\u00a0'
            : observedAt === null
              ? noData
              : t('catalog.spec.dataDate', { date: observedAt })}
        </p>
      </div>
    </>
  );

  return (
    <article
      className={[styles.card, teaser && styles.cardTeaser].filter(Boolean).join(' ')}
      data-access={vehicle.access}
    >
      {href === null ? (
        <div className={styles.link}>{body}</div>
      ) : (
        <Link to={href} className={styles.link}>
          {body}
        </Link>
      )}

      {favorite ? <div className={styles.favoriteSlot}>{favorite}</div> : null}
      {notice ? <div className={styles.notice}>{notice}</div> : null}
      {action ? <div className={styles.action}>{action}</div> : null}
    </article>
  );
}
